import React, { useState, useEffect } from 'react';
import { Section } from '../../stores/builderStore';
import { RefreshCw, ExternalLink, Smartphone, Tablet, Monitor } from 'lucide-react';

interface PreviewProps {
  sections: Section[];
  device?: 'desktop' | 'tablet' | 'mobile';
}

export const Preview: React.FC<PreviewProps> = ({ sections, device = 'desktop' }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const deviceFrames = {
    desktop: {
      width: '100%',
      height: '100%',
      scale: 1,
      frame: false
    },
    tablet: {
      width: '768px',
      height: '1024px',
      scale: 0.8,
      frame: true
    },
    mobile: {
      width: '375px',
      height: '812px',
      scale: 0.8,
      frame: true
    }
  };

  const currentDevice = deviceFrames[device];

  const handleRefresh = () => {
    setIsLoading(true);
    setRefreshKey(prev => prev + 1);
    setTimeout(() => setIsLoading(false), 500);
  };

  const openInNewTab = () => {
    const previewUrl = `https://clarityofconcept.com/homepage/preview?device=${device}`;
    window.open(previewUrl, '_blank');
  };

  // Generate preview HTML
  const generatePreviewHTML = () => {
    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Homepage Preview</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
          }
          .animate-fadeIn {
            animation: fadeIn 0.5s ease-in-out;
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        </style>
      </head>
      <body>
        <div id="preview-container">
          ${sections.map(section => renderSection(section)).join('')}
        </div>
      </body>
      </html>
    `;
    return html;
  };

  const renderSection = (section: Section): string => {
    if (!section.is_visible) return '';

    const styles = Object.entries(section.styles || {})
      .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
      .join('; ');

    switch (section.type) {
      case 'hero':
      case 'hero_center':
      case 'hero_banner': // Map database type
        return `
          <section class="relative overflow-hidden" style="height: ${section.props.height || '600px'}; ${styles}">
            ${section.props.backgroundImage ? `
              <img src="${section.props.backgroundImage}" alt="" class="absolute inset-0 w-full h-full object-cover" />
              <div class="absolute inset-0 bg-black bg-opacity-40"></div>
            ` : ''}
            <div class="relative h-full flex flex-col justify-center items-${section.props.alignment || 'center'} text-white px-8 py-12">
              <h1 class="text-5xl font-bold mb-4">${section.props.title || ''}</h1>
              <p class="text-xl mb-8">${section.props.subtitle || ''}</p>
              ${section.props.buttonText ? `
                <a href="${section.props.buttonLink || '#'}" class="px-8 py-4 bg-white text-blue-600 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                  ${section.props.buttonText}
                </a>
              ` : ''}
            </div>
          </section>
        `;

      case 'features':
      case 'feature_cards': // Map database type
        const cols = section.props.columns || 3;
        return `
          <section class="py-16 px-8" style="${styles}">
            ${section.props.title ? `<h2 class="text-4xl font-bold text-center mb-12">${section.props.title}</h2>` : ''}
            <div class="grid grid-cols-${cols} gap-8 max-w-6xl mx-auto">
              ${(section.props.items || []).map((item: any) => `
                <div class="text-center">
                  <div class="text-5xl mb-4">${item.icon || '⭐'}</div>
                  <h3 class="text-xl font-semibold mb-2">${item.title || ''}</h3>
                  <p class="text-gray-600">${item.description || ''}</p>
                </div>
              `).join('')}
            </div>
          </section>
        `;

      case 'cta':
        return `
          <section class="py-16 px-8 text-center" style="${styles}">
            <h2 class="text-4xl font-bold mb-4">${section.props.title || ''}</h2>
            <p class="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">${section.props.subtitle || ''}</p>
            <div class="flex gap-4 justify-center">
              ${section.props.buttonText ? `
                <a href="${section.props.buttonLink || '#'}" class="px-8 py-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
                  ${section.props.buttonText}
                </a>
              ` : ''}
              ${section.props.secondaryButtonText ? `
                <a href="${section.props.secondaryButtonLink || '#'}" class="px-8 py-4 border-2 border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors">
                  ${section.props.secondaryButtonText}
                </a>
              ` : ''}
            </div>
          </section>
        `;

      case 'testimonials_carousel':
      case 'carousel': // Map database type
        return `
          <section class="py-16 px-8" style="${styles}">
            ${section.props.title ? `<h2 class="text-4xl font-bold text-center mb-12">${section.props.title}</h2>` : ''}
            <div class="max-w-3xl mx-auto">
              ${(section.props.items || []).map((item: any, index: number) => `
                <div class="bg-gray-50 rounded-lg p-8 ${index > 0 ? 'hidden' : ''}">
                  <p class="text-lg italic mb-6">"${item.quote || ''}"</p>
                  <div class="flex items-center">
                    ${item.avatar ? `
                      <img src="${item.avatar}" alt="${item.author}" class="w-12 h-12 rounded-full mr-4" />
                    ` : `
                      <div class="w-12 h-12 bg-gray-300 rounded-full mr-4"></div>
                    `}
                    <div>
                      <p class="font-semibold">${item.author || ''}</p>
                      <p class="text-sm text-gray-600">${item.role || ''}</p>
                    </div>
                  </div>
                </div>
              `).join('')}
            </div>
          </section>
        `;

      case 'gallery':
      case 'content_grid': // Map database type
        const galCols = section.props.columns || 4;
        return `
          <section class="py-16 px-8" style="${styles}">
            ${section.props.title ? `<h2 class="text-4xl font-bold text-center mb-12">${section.props.title}</h2>` : ''}
            <div class="grid grid-cols-${galCols} gap-4 max-w-6xl mx-auto">
              ${(section.props.images || []).map((image: any) => `
                <div class="aspect-square overflow-hidden rounded-lg">
                  ${image.url || image ? `
                    <img src="${image.url || image}" alt="${image.caption || ''}" class="w-full h-full object-cover hover:scale-110 transition-transform duration-300" />
                  ` : `
                    <div class="w-full h-full bg-gray-200"></div>
                  `}
                </div>
              `).join('')}
            </div>
          </section>
        `;

      case 'stats':
        return `
          <section class="py-16 px-8 bg-gray-50" style="${styles}">
            <div class="grid grid-cols-4 gap-8 max-w-4xl mx-auto text-center">
              ${(section.props.items || []).map((item: any) => `
                <div>
                  <div class="text-4xl font-bold text-blue-600 mb-2">${item.number || '0'}</div>
                  <div class="text-gray-600">${item.label || ''}</div>
                </div>
              `).join('')}
            </div>
          </section>
        `;

      case 'pricing':
        return `
          <section class="py-16 px-8" style="${styles}">
            ${section.props.title ? `<h2 class="text-4xl font-bold text-center mb-12">${section.props.title}</h2>` : ''}
            <div class="grid grid-cols-3 gap-8 max-w-5xl mx-auto">
              ${(section.props.plans || []).map((plan: any) => `
                <div class="border ${plan.highlighted ? 'border-blue-500 shadow-xl' : 'border-gray-200'} rounded-lg p-6">
                  ${plan.highlighted ? '<div class="text-center text-blue-600 text-sm font-semibold mb-2">MOST POPULAR</div>' : ''}
                  <h3 class="text-2xl font-bold mb-2">${plan.name || ''}</h3>
                  <div class="text-3xl font-bold mb-4">
                    ${plan.price || '$0'}
                    <span class="text-sm text-gray-600">/${plan.period || 'month'}</span>
                  </div>
                  <ul class="space-y-2 mb-8">
                    ${(plan.features || []).map((feature: string) => `
                      <li class="flex items-center">
                        <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                        </svg>
                        ${feature}
                      </li>
                    `).join('')}
                  </ul>
                  <button class="w-full py-3 ${plan.highlighted ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'} rounded-lg font-medium hover:opacity-90 transition-opacity">
                    ${plan.buttonText || 'Choose Plan'}
                  </button>
                </div>
              `).join('')}
            </div>
          </section>
        `;

      case 'quick_actions':
        return `
          <section class="py-16 px-8 bg-gray-50" style="${styles}">
            ${section.props.title ? `<h2 class="text-4xl font-bold text-center mb-12">${section.props.title}</h2>` : ''}
            <div class="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              ${(section.props.actions || [
                { icon: '📚', label: 'Study Materials' },
                { icon: '📝', label: 'Mock Tests' },
                { icon: '📊', label: 'Analytics' },
                { icon: '💬', label: 'Discussions' }
              ]).map((action: any) => `
                <button class="p-6 bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                  <div class="text-4xl mb-3">${action.icon || '⚡'}</div>
                  <div class="font-medium">${action.label || 'Action'}</div>
                </button>
              `).join('')}
            </div>
          </section>
        `;

      case 'announcement':
        return `
          <section class="py-8 px-8" style="${styles}">
            <div class="max-w-4xl mx-auto p-6 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
              <div class="flex items-start">
                <div class="text-3xl mr-4">📢</div>
                <div class="flex-1">
                  <h3 class="text-xl font-semibold text-blue-900 mb-2">
                    ${section.props.title || 'Important Announcement'}
                  </h3>
                  <p class="text-blue-800">
                    ${section.props.message || section.props.subtitle || 'Stay tuned for important updates.'}
                  </p>
                  ${section.props.buttonText ? `
                    <a href="${section.props.buttonLink || '#'}" class="inline-block mt-4 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                      ${section.props.buttonText}
                    </a>
                  ` : ''}
                </div>
              </div>
            </div>
          </section>
        `;

      default:
        return `
          <section class="py-16 px-8" style="${styles}">
            <div class="text-center text-gray-500">
              <p class="text-lg">${section.title}</p>
              <p class="text-sm">Section type: ${section.type}</p>
            </div>
          </section>
        `;
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-100">
      {/* Preview Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="font-semibold">Live Preview</h3>
          <div className="flex items-center gap-2">
            {device === 'desktop' && <Monitor className="w-4 h-4" />}
            {device === 'tablet' && <Tablet className="w-4 h-4" />}
            {device === 'mobile' && <Smartphone className="w-4 h-4" />}
            <span className="text-sm text-gray-600 capitalize">{device} View</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh preview"
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={openInNewTab}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Open in new tab"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Preview Content */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-auto">
        <div
          className={`bg-white shadow-2xl ${currentDevice.frame ? 'rounded-[2.5rem] border-8 border-gray-800' : 'w-full h-full'}`}
          style={{
            width: currentDevice.width,
            height: currentDevice.height,
            transform: `scale(${currentDevice.scale})`,
            transformOrigin: 'top center'
          }}
        >
          {/* Device Notch (for mobile) */}
          {device === 'mobile' && (
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-40 h-7 bg-gray-800 rounded-b-3xl"></div>
          )}

          {/* Preview iframe */}
          <iframe
            key={refreshKey}
            srcDoc={generatePreviewHTML()}
            className="w-full h-full rounded-[2rem] bg-white"
            title="Homepage Preview"
            style={{ border: 'none' }}
            sandbox="allow-scripts"
          />
        </div>
      </div>

      {/* Preview Status */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
            <p className="text-sm text-gray-600">Refreshing preview...</p>
          </div>
        </div>
      )}
    </div>
  );
};